import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api/client";
import { usePermission } from "../../../hooks/usePermission";
import { PERMISSIONS } from "../../../constants/permissions";
import useAuth from "../../../hooks/useAuth";
import MetricCardBase from "../components/MetricCard";
import StatusBadgeBase from "../components/StatusBadge";
import Tabs from "../components/Tabs";
import LockableButton from "../components/LockableButton";
import Toast from "../components/Toast";
import { verifyModuleEnabled } from "../utils/moduleAccess";
import InventoryOverviewTab from "./dashboard/InventoryOverviewTab";
import AssetOverviewTab from "./dashboard/AssetOverviewTab";
import "./Dashboard.css";
import { activeStagesWithPart } from "../components/activeStageUtils";

const ROWS_PER_PAGE = 10;

const STATUS_BADGE = {
  scheduled: { bg: "#f1f5f9", color: "#475569", label: "Scheduled" },
  active:    { bg: "#dbeafe", color: "#1d4ed8", label: "Active" },
  at_risk:   { bg: "#fff7ed", color: "#c2410c", label: "At Risk" },
  overdue:   { bg: "#fee2e2", color: "#dc2626", label: "Overdue" },
  completed: { bg: "#dcfce7", color: "#15803d", label: "Completed" },
};

function MetricCard(props) {
  return <MetricCardBase className="dash-metric-card" {...props} />;
}

function StatusBadge({ status }) {
  return <StatusBadgeBase status={status} colorMap={STATUS_BADGE} />;
}

function POOverviewTab() {
  const navigate = useNavigate();
  const canCreatePO = usePermission(PERMISSIONS.MANUFACTURING_PO_CREATE);
  const { enabledModules, setEnabledModules } = useAuth();
  const hasPOModule = !enabledModules || enabledModules.includes("po");
  const [toast, setToast] = useState(null);
  const handleNewPOClick = async () => {
    const { allowed, message } = await verifyModuleEnabled("po", { enabledModules, setEnabledModules });
    if (!allowed) {
      setToast({ msg: message, type: "error" });
      return;
    }
    navigate("/manufacturing/po/new");
  };
  const [summary, setSummary] = useState(null);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);
  const elapsedRef = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [summaryRes, posRes] = await Promise.all([
        api.get("/manufacturing/pos/summary"),
        api.get("/manufacturing/pos"),
      ]);
      setSummary(summaryRes.data.data || null);
      setPos(posRes.data.data || []);
      setLastUpdated(new Date());
      setElapsed(0);
      setPage(0);
    } catch {
      // handled by empty state
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    intervalRef.current = setInterval(() => load(true), 60000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  useEffect(() => {
    elapsedRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(elapsedRef.current);
  }, []);

  const livePos = pos.filter((po) =>
    po.status === "active" || po.status === "scheduled" ||
    po.status === "at_risk" || po.status === "overdue"
  );

  const totalPages = Math.ceil(livePos.length / ROWS_PER_PAGE);
  const paginatedPos = livePos.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  const elapsedLabel = elapsed < 60 ? `${elapsed}s ago` : `${Math.floor(elapsed / 60)}m ago`;

  return (
    <div>
      <div className="dash-header">
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Production Overview</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Live status of all production orders.</p>
        </div>
        {canCreatePO && (
          <LockableButton
            className="btn-primary"
            style={{ whiteSpace: "nowrap" }}
            onClick={handleNewPOClick}
            locked={!hasPOModule}
            lockedTitle="Purchase Order module is not enabled for your company"
          >
            + New PO
          </LockableButton>
        )}
      </div>

      <div className="dash-metrics">
        <MetricCard label="Active POs" value={summary?.activePOs} />
        <MetricCard label="Parts in Progress" value={summary?.partsInProgress != null ? summary.partsInProgress.toLocaleString() : (loading ? "—" : "0")} />
        <MetricCard label="Overdue Jobs" value={summary?.overdueJobs} accent={summary?.overdueJobs > 0 ? "#dc2626" : undefined} />
        <MetricCard label="Completed This Month" value={summary?.completedThisMonth} />
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Live PO Tracker</h2>
          {canCreatePO && (
            <LockableButton
              className="btn-primary"
              style={{ fontSize: 13, padding: "5px 14px" }}
              onClick={handleNewPOClick}
              locked={!hasPOModule}
              lockedTitle="Purchase Order module is not enabled for your company"
            >
              + New PO
            </LockableButton>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : livePos.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <p style={{ color: "#94a3b8", marginBottom: 16 }}>No active purchase orders.</p>
            {canCreatePO && (
              <LockableButton
                className="btn-primary"
                onClick={handleNewPOClick}
                locked={!hasPOModule}
                lockedTitle="Purchase Order module is not enabled for your company"
              >
                + Create your first PO
              </LockableButton>
            )}
          </div>
        ) : (
          <>
            {/* Desktop / tablet: scrollable table */}
            <div className="dash-table-wrap">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["PO Number", "Part Type", "Total Qty", "Active Stages", "Deadline", "Status"].map((h) => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedPos.map((po) => {
                    const partTypes = [...new Set((po.parts || []).map((p) => p.partType))].join(", ");
                    const totalQty = (po.parts || []).reduce((sum, p) => sum + p.quantity, 0);
                    const activeStages = activeStagesWithPart(po);
                    const deadlineStr = po.deliveryDate ? po.deliveryDate.split("T")[0] : "";
                    const isOverdue = po.status === "overdue" || (deadlineStr && deadlineStr < today && po.status !== "completed");
                    const deadlineRed = isOverdue || po.status === "at_risk";

                    return (
                      <tr key={po.id} style={{ borderBottom: "1px solid #f1f5f9", background: isOverdue ? "#fff5f5" : "transparent" }}>
                        <td style={{ padding: "10px 16px" }}>
                          <Link to={`/manufacturing/po/${po.id}`} style={{ color: "#1d4ed8", fontWeight: 700, textDecoration: "none" }}>
                            {po.poNumber}
                          </Link>
                        </td>
                        <td style={{ padding: "10px 16px", color: "#64748b", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{partTypes || "—"}</td>
                        <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>{totalQty.toLocaleString()}</td>
                        <td style={{ padding: "10px 16px" }}>
                          {activeStages.length === 0 ? (
                            <span style={{ color: "#94a3b8" }}>{po.status === "scheduled" ? "Not started" : "—"}</span>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {activeStages.map((s) => (
                                <div key={s.id}>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: s.status === "overdue" ? "#dc2626" : "#475569", fontWeight: 600 }}>
                                    {s.status === "overdue" && <span title="Overdue">⚠️</span>}
                                    {s.partType && <span style={{ color: "#94a3b8", fontWeight: 600 }}>{s.partType} ·</span>}
                                    {s.stageName}
                                  </span>
                                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                                    {s.partsCompleted} done · {s.partsRemaining} remaining
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 16px", color: deadlineRed ? "#dc2626" : "#0f2d5c", fontWeight: deadlineRed ? 700 : 400 }}>{deadlineStr || "—"}</td>
                        <td style={{ padding: "10px 16px" }}><StatusBadge status={po.status} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <div className="dash-po-cards">
              {paginatedPos.map((po) => {
                const activeStages = activeStagesWithPart(po);
                const deadlineStr = po.deliveryDate ? po.deliveryDate.split("T")[0] : "";
                const isOverdue = po.status === "overdue" || (deadlineStr && deadlineStr < today && po.status !== "completed");
                const deadlineRed = isOverdue || po.status === "at_risk";

                return (
                  <div key={po.id} className={`dash-po-card${isOverdue ? " overdue" : po.status === "at_risk" ? " at-risk" : ""}`}>
                    <div className="dash-po-card-top">
                      <Link to={`/manufacturing/po/${po.id}`} style={{ color: "#1d4ed8", fontWeight: 700, fontSize: 15, textDecoration: "none" }}>
                        {po.poNumber}
                      </Link>
                      <StatusBadge status={po.status} />
                    </div>
                    {activeStages.length > 0 && (
                      <div className="dash-po-card-stage">
                        {activeStages.map((s) => (
                          <div key={s.id} style={{ marginBottom: 4 }}>
                            <div style={{ color: s.status === "overdue" ? "#dc2626" : "#475569", fontWeight: 600 }}>
                              {s.status === "overdue" ? "⚠️ " : ""}{s.partType ? `${s.partType} · ` : ""}{s.stageName}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>
                              {s.partsCompleted} done · {s.partsRemaining} remaining
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {activeStages.length === 0 && po.status === "scheduled" && (
                      <div className="dash-po-card-stage" style={{ color: "#94a3b8" }}>Not started</div>
                    )}
                    {deadlineStr && (
                      <div className="dash-po-card-deadline" style={{ color: deadlineRed ? "#dc2626" : "#64748b", fontWeight: deadlineRed ? 700 : 400 }}>
                        Due {deadlineStr}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination + last updated */}
            <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {lastUpdated ? `Last updated: ${elapsedLabel}` : ""}
              </span>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    style={{ padding: "4px 10px", border: "1px solid #d6dfed", borderRadius: 6, background: page === 0 ? "#f8fafc" : "#fff", cursor: page === 0 ? "default" : "pointer", color: "#64748b", fontSize: 13 }}
                  >‹</button>
                  <span style={{ fontSize: 13, color: "#64748b", padding: "0 6px" }}>{page + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    style={{ padding: "4px 10px", border: "1px solid #d6dfed", borderRadius: 6, background: page >= totalPages - 1 ? "#f8fafc" : "#fff", cursor: page >= totalPages - 1 ? "default" : "pointer", color: "#64748b", fontSize: 13 }}
                  >›</button>
                </div>
              )}
              {totalPages <= 1 && lastUpdated && (
                <span style={{ fontSize: 12, color: "#94a3b8" }}>
                  {livePos.length} order{livePos.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </>
        )}
      </div>
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState("po");
  const canViewInventory = usePermission(PERMISSIONS.MANUFACTURING_INVENTORY_VIEW);
  // Anyone who can open Asset Management sees its tab — but only while the
  // superadmin has the module switched on.
  const { enabledModules } = useAuth();
  const canViewAssets =
    usePermission([
      PERMISSIONS.MANUFACTURING_ASSET_VIEW,
      PERMISSIONS.MANUFACTURING_ASSET_MANAGE,
      PERMISSIONS.MANUFACTURING_ASSET_MAINTAIN,
      PERMISSIONS.MANUFACTURING_ASSET_BREAKDOWN_REPORT,
    ]) && Boolean(enabledModules?.includes("assets"));
  const dashboardTabs = [
    { key: "po", label: "PO" },
    ...(canViewInventory ? [{ key: "inventory", label: "Inventory" }] : []),
    ...(canViewAssets ? [{ key: "assets", label: "Assets" }] : []),
  ];
  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <Tabs tabs={dashboardTabs} active={tab} onChange={setTab} />
      {tab === "po" && <POOverviewTab />}
      {tab === "inventory" && canViewInventory && <InventoryOverviewTab />}
      {tab === "assets" && canViewAssets && <AssetOverviewTab />}
    </div>
  );
}
